import React from 'react';
import Svg, {
  Circle, G, Text as SvgText,
} from 'react-native-svg';
import { View, Text } from 'react-native';
import Colors from '../../constants/Colors';

type ChartDatum = {
  index: number;
  value: number;
  date: string;
  label: string;
};

const SEGMENT_COLORS = [
  Colors.light.hereworksGreen400,
  Colors.light.hereworksYellow500,
  Colors.light.hereworksBlue400,
  Colors.light.hereworksOrange,
  Colors.light.hereworksPurple500,
  Colors.light.hereworksRed300,
  Colors.light.hereworksExtraDarkTeal,
];

const polarToCartesian = (
  centerX: number,
  centerY: number,
  radius: number,
  angleInRadians: number,
) => ({
  x: centerX + radius * Math.cos(angleInRadians),
  y: centerY + radius * Math.sin(angleInRadians),
});

const PieChart = ({ chartData }: { chartData: ChartDatum[] }) => {
  const radius = 50;
  const strokeWidth = 100;
  const center = radius + 50;
  const labelRadius = radius + 20;

  const total = chartData.reduce((sum, d) => sum + d.value, 0);
  if (total === 0) return null;

  const circumference = 2 * Math.PI * radius;
  let cumulativeValue = 0;

  return (
    <View>
      <Svg width={center * 2} height={center * 2}>
        <G transform={`translate(${center} ${center})`}>
          {chartData.map((item, i) => {
            const valueFraction = item.value / total;
            const segmentLength = valueFraction * circumference;

            const startAngle = (cumulativeValue / total) * 2 * Math.PI;
            const midAngle = startAngle + valueFraction * Math.PI;

            cumulativeValue += item.value;

            const labelPos = polarToCartesian(0, 0, labelRadius + 12, midAngle);

            const textAnchor = Math.cos(midAngle) >= 0 ? 'start' : 'end';

            return (
              <React.Fragment key={item.index}>
                <Circle
                  r={radius}
                  stroke={SEGMENT_COLORS[i % SEGMENT_COLORS.length]}
                  strokeWidth={strokeWidth}
                  fill="transparent"
                  strokeDasharray={`${segmentLength} ${circumference - segmentLength}`}
                  strokeDashoffset={-(startAngle / (2 * Math.PI)) * circumference}
                  strokeLinecap="butt"
                />
                <SvgText
                  x={labelPos.x}
                  y={labelPos.y}
                  fontSize={10}
                  fill={'#000000'}
                  alignmentBaseline="middle"
                  textAnchor={textAnchor}
                >
                  {item.value}
                </SvgText>
              </React.Fragment>
            );
          })}
        </G>
      </Svg>
      <View style={{ marginTop: 16 }}>
          {chartData.map((item, i) => (
            <View key={item.index} style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 4 }}>
              <View
                style={{
                  width: 12,
                  height: 12,
                  backgroundColor: SEGMENT_COLORS[i % SEGMENT_COLORS.length],
                  marginRight: 8,
                }}
              />
              <Text style={{ fontSize: 12, color: '#333' }}>{`${item.label}: ${item.value}`}</Text>
            </View>
          ))}
        </View>
      </View>
  );
};

export default PieChart;
